import { Component, NgZone, OnInit } from '@angular/core';
import { RootState } from '../../store/store.state';
import { select, Store } from '@ngrx/store';
import { animate, style, transition, trigger } from '@angular/animations';
import { selectAlerts } from '../store/alerts.selectors';
import { RemoveAlert } from '../store/alerts.action';
import { takeUntil } from 'rxjs/operators';
import { BaseAbstractComponent } from '../../shared/base/base-abstract.component';
import { Alert } from '../../../../common/models/alert.interface';
import { Observable } from 'rxjs';

@Component({
    standalone: false,
    selector: 'q-alert-detail',
    templateUrl: './alert-detail.component.html',
    styleUrls: ['./alert-detail.component.scss'],
    animations: [
        trigger('flyInOut', [
            transition(':enter', [
                style({
                    opacity: 0,
                    transform: 'perspective(400px) rotateX(90deg)'
                }),
                animate(
                    '200ms ease-out',
                    style({
                        opacity: 1,
                        transform: 'perspective(400px) rotateX(0deg)'
                    })
                )
            ]),
            transition(':leave', [
                animate(
                    '200ms ease-in',
                    style({
                        opacity: 0
                    })
                )
            ])
        ])
    ]
})
export class AlertDetailComponent
    extends BaseAbstractComponent
    implements OnInit
{
    alerts$: Observable<Alert[]>;
    MAX_VISIBLE = 2;
    private alertTimeouts = new Map<number, ReturnType<typeof setTimeout>>();

    constructor(
        private store: Store<RootState>,
        private ngZone: NgZone
    ) {
        super();

        this.alerts$ = this.store.pipe(select(selectAlerts));

        this.alerts$.pipe(takeUntil(this.$destroy)).subscribe((alerts) => {
            this.setupAutoRemove(alerts || []);
        });
    }

    ngOnInit() {}

    handleAlertClosed(alert: Alert) {
        this.clearAlertTimeout(alert.id);
        this.store.dispatch(new RemoveAlert(alert.id));
    }

    setupAutoRemove(alerts: Alert[]) {
        // Remove timeouts for alerts that no longer exist
        for (const id of Array.from(this.alertTimeouts.keys())) {
            if (!alerts.find((alert) => alert.id === id)) {
                this.clearAlertTimeout(id);
            }
        }
        // Set timeouts for new alerts
        for (const alert of alerts) {
            if (!this.alertTimeouts.has(alert.id)) {
                const timeout = setTimeout(() => {
                    this.ngZone.run(() => {
                        this.handleAlertClosed(alert);
                    });
                }, alert.timer ?? 3000);

                this.alertTimeouts.set(alert.id, timeout);
            }
        }
    }

    private clearAlertTimeout(id: number) {
        const timeout = this.alertTimeouts.get(id);

        if (timeout) {
            clearTimeout(timeout);
            this.alertTimeouts.delete(id);
        }
    }

    trackByAlertId(index: number, alert: Alert): number {
        return alert.id;
    }

    preOnDestroy() {
        for (const id of Array.from(this.alertTimeouts.keys())) {
            this.clearAlertTimeout(id);
        }
    }
}
